import { useCallback } from 'react';
import clsx from 'clsx';

import { useAsyncAssetProps } from '@app/contexts/async-asset-props';
import type { AttachmentBoxData } from '@flow/libs/data-validators/entity-schemas/document-box/attachment-box';
import type { Asset } from '@app/reducers/current-contract';

import { generateDocumentOpenURL } from '@app/components/contract-boxes/generic-box-helpers';
import Button from '@app/components/button';
import OpenFile from '@app/components/icons/open-file';
import Remove from '@app/components/icons/delete';
import { T } from '@app/components/translation';

import style from './attachment-file.module.scss';

type Props = {
  contractId: Oneflow.Agreement['id'];
  guestToken: string;
  hidden?: boolean;
  isAllowedToRemoveBoxData: boolean;
  onAttachmentRemove: () => void;
  uploadingStatus: Asset['status'] | undefined;
  value: AttachmentBoxData['value'];
};

const AttachmentActionsContainer = ({
  contractId,
  guestToken,
  hidden,
  isAllowedToRemoveBoxData,
  onAttachmentRemove,
  uploadingStatus,
  value,
}: Props) => {
  const { succeededAsyncAssetData } = useAsyncAssetProps();
  const showOpenButton =
    !value.status ||
    uploadingStatus === 'ready' ||
    value.status === 'ready' ||
    (succeededAsyncAssetData &&
      succeededAsyncAssetData.assetId === value.assetId &&
      succeededAsyncAssetData.status === 'ready');

  const className = clsx(style.ActionsContainer, {
    [style.Hidden]: hidden,
  });

  const renderOpenButton = useCallback(() => {
    if (showOpenButton) {
      return (
        <Button
          icon={OpenFile}
          href={generateDocumentOpenURL(contractId, value, guestToken)}
          download
          customClass={style.Hover}
        >
          <T
            _str="Open"
            _comment="This text shows that you can download the file"
          />
        </Button>
      );
    }

    return null;
  }, [
    contractId,
    guestToken,
    showOpenButton,
    value,
  ]);

  return (
    <div className={className}>
      {renderOpenButton()}
      {isAllowedToRemoveBoxData && (
        <Button
          icon={Remove}
          onClick={onAttachmentRemove}
          customClass={style.Remove}
        >
          <T
            _str="Remove"
            _comment="This text shows that you can remove the file from here"
          />
        </Button>
      )}
    </div>
  );
};

export default AttachmentActionsContainer;
